import { IDetailsRowBaseProps } from '@fluentui/react';
import { AmountTextView } from 'common/components/AmountView/AmountTextView';
import { ColumnData } from 'common/components/SearchBar';
import { TABLE_ROWS } from 'common/constants';
import { getSortedColumns } from 'common/utils/columnUtilities';
import { OrderDirection, SortOrder } from 'common/utils/commonTypes';
import { dateConvertions, dateFormat } from 'common/utils/dateFormats';
import { TripBreakdownItem } from '..';
import {
  TripBreakdownSearch_Query,
  TripBreakdownSearchVariables
} from '../__generated__/TripBreakdownSearch';
import { useStyles } from '../index.styles';
import { toOrderByVariable, toTripBreakdownFilterVariable } from '../utils';

export function transformTripBreakdownData(
  nodes?: TripBreakdownItem[]
): TripBreakdownItem[] | undefined {
  return nodes?.map((item) => ({
    ...item,
    travelAuthorizationDate: item?.travelAuthorizationDate
      ? dateFormat(dateConvertions(item.travelAuthorizationDate))
      : null,
  }));
}



export const _renderDetailsFooterItemColumn = (
  data: TripBreakdownSearch_Query | undefined
): IDetailsRowBaseProps['onRenderItemColumn'] => {
  return (_item, _index, column) => {
    const styles = useStyles();
    const { aggregates } = {
      ...data?.tripBreakdownSearch,
    };

    let fieldContent;

    switch (column?.key) {
      case 'airfareAmount':
        fieldContent = aggregates?.sum?.airfareAmount;
        break;
      case 'accommodationAmount':
        fieldContent = aggregates?.sum?.accommodationAmount;
        break;
      case 'vehicleRentalAmount':
        fieldContent = aggregates?.sum?.vehicleRentalAmount;
        break;
      case 'transportationAmount':
        fieldContent = aggregates?.sum?.transportationAmount;
        break;
      case 'trainAmount':
        fieldContent = aggregates?.sum?.trainAmount;
        break;
      case 'incidentalAmount':
        fieldContent = aggregates?.sum?.incidentalAmount;
        break;
      case 'controlTotalAmount':
        fieldContent = aggregates?.sum?.controlTotalAmount;
        break;
      case '_companionAmountUsed':
        fieldContent = aggregates?.sum?._companionAmountUsed;
        break;
      case '_companionTicketUsed':
        fieldContent = aggregates?.sum?._companionTicketUsed;
        break;
      default:
        fieldContent = '';
    }

    return (
      <AmountTextView
        variant="medium"
        className={styles.footerTotal}
        value={fieldContent || ''}
      />
    );
  };
};

interface PerformTripBreakdownSearchOptions {
  showMore: boolean;
  globalValue: boolean;
  searchTextValue: string | null;
  searchFilters: any[] | undefined;
  sortOrderParam: any;
  data: any; // optionally narrow the type
  fetchMore?: (options: { variables: TripBreakdownSearchVariables }) => void;
  refetch: (variables: TripBreakdownSearchVariables) => void;
  setUrlParameters?: (
    params: Record<string, string | undefined>,
    method: 'replace' | 'push'
  ) => void;
}

export function performTripBreakdownSearch({
  showMore,
  globalValue,
  searchTextValue,
  searchFilters,
  sortOrderParam,
  data,
  fetchMore,
  refetch,
  setUrlParameters,
}: PerformTripBreakdownSearchOptions) {
  const filter = searchFilters?.length
    ? ({
        and: toTripBreakdownFilterVariable(searchFilters),
      } as TripBreakdownSearchVariables['filter'])
    : undefined;

  const searchRequest = searchTextValue
    ? { searchText: [searchTextValue || ''] }
    : undefined;

  const searchQueryParam =
    searchTextValue && searchTextValue.length > 0 ? searchTextValue : undefined;

  const variables: TripBreakdownSearchVariables = {
    first: TABLE_ROWS,
    after: showMore
      ? data?.tripBreakdownSearch?.pageInfo?.endCursor
      : undefined,
    orderBy: toOrderByVariable(sortOrderParam),
    filter,
    searchRequest,
    isLinkSearch: globalValue,
  };

  setUrlParameters?.({ t: searchQueryParam }, 'replace');

  if (showMore) {
    fetchMore?.({ variables });
  } else {
    refetch(variables);
  }
}

export function handleColumnClick(
  clickedColumn: ColumnData | undefined,
  gridColumns: ColumnData[],
  setGridColumns: (cols: ColumnData[]) => void,
  setSortOrderParam: (sort: SortOrder) => void
): void {
  if (clickedColumn) {
    const { newColumns, desc } = getSortedColumns(clickedColumn, gridColumns);
    setGridColumns(newColumns);

    setSortOrderParam({
      column: clickedColumn.key,
      direction: desc ? OrderDirection.DESC : OrderDirection.ASC,
    });
  }
}
